import { useEffect, useId, useMemo, useState, type CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { activeNav, NAV, SETTINGS_LINK, SIDE_SECTIONS, type NavItem } from '../nav.js';
import { getInbox, listProjects } from '../lifeos/api.js';
import type { Project } from '../lifeos/types.js';
import { useWorkspaces } from '../lib/workspaces.js';
import { projectTree } from './JobTabs.js';
import Icon from '../ui/Icon.js';

const OPEN_KEY = 'vitals.sidebar.open';

function readOpen(): Set<string> {
  try {
    const raw = window.localStorage.getItem(OPEN_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeOpen(s: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(OPEN_KEY, JSON.stringify([...s]));
  } catch {
    /* private mode: expansion just isn't remembered */
  }
}

/** True on a Mac-ish platform, for the ⌘K vs Ctrl K hint. */
export function isMac(): boolean {
  try {
    return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  } catch {
    return false;
  }
}

/**
 * The sidebar's navigation body (spec 2026-10-14 §sidebar): main destinations,
 * one row per workspace (expandable to its projects), then the flat hub rows
 * (Notes, Money, Health, Fitness, Coffee, Home), Settings last. Hub pages carry
 * their siblings as tabs (nav.ts HUBS), so nothing here folds any more. Shared by the
 * desktop rail and the phone's menu sheet.
 */
export function SidebarNav(): JSX.Element {
  const { pathname } = useLocation();
  const active = activeNav(pathname);
  const workspaces = useWorkspaces();
  const [projects, setProjects] = useState<Project[]>([]);
  const [inboxCount, setInboxCount] = useState(0);
  const [open, setOpen] = useState<Set<string>>(readOpen);
  const headId = useId();

  useEffect(() => {
    let alive = true;
    listProjects().then(
      (p) => alive && setProjects(p),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, []);

  // Inbox badge: what still needs a look, refreshed on navigation.
  useEffect(() => {
    let alive = true;
    getInbox({ status: 'new', limit: 1 }).then(
      (r) => alive && setInboxCount((r.counts?.new ?? 0) + (r.counts?.needsReply ?? 0)),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [pathname]);

  const toggle = (key: string): void =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeOpen(next);
      return next;
    });

  const bySphere = useMemo(() => {
    const m = new Map<string, Project[]>();
    for (const p of projects) {
      if (p.status === 'archived' || p.status === 'done') continue;
      m.set(p.sphereKey, [...(m.get(p.sphereKey) ?? []), p]);
    }
    return m;
  }, [projects]);

  const item = (l: NavItem, badge?: number): JSX.Element => {
    const on = active === l.to;
    return (
      <Link key={l.to} to={l.to} aria-current={on ? 'page' : undefined} className={`nav__item${on ? ' nav__item--active' : ''}`}>
        <Icon name={l.icon} size={20} />
        <span className="nav__label">{l.label}</span>
        {badge ? (
          <span className="nav__badge sb-badge" aria-label={`${badge} new`}>
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </Link>
    );
  };

  return (
    <nav className="sb" aria-label="Primary">
      <div className="nav sb-main">{NAV.map((l) => item(l, l.to === '/inbox' ? inboxCount : undefined))}</div>

      {workspaces.length > 0 && (
        <section className="sb-sec" aria-labelledby={headId}>
          <h2 className="sb-sec__title" id={headId}>
            Workspaces
          </h2>
          <ul className="sb-ws">
            {workspaces.map((w) => {
              const key = `ws:${w.key}`;
              const isOpen = open.has(key);
              const href = `/w/${encodeURIComponent(w.key)}`;
              const on = pathname === href;
              const kids = projectTree(bySphere.get(w.key) ?? []);
              return (
                <li key={w.key} className="sb-ws__item" style={{ '--job': w.color ?? 'var(--text-muted)' } as CSSProperties}>
                  <div className={`sb-ws__row${on ? ' sb-ws__row--on' : ''}`}>
                    <button
                      type="button"
                      className={`sb-chev${isOpen ? ' sb-chev--open' : ''}`}
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? 'Hide' : 'Show'} ${w.name} projects`}
                      onClick={() => toggle(key)}
                    >
                      <Icon name="chevron-right" size={14} />
                    </button>
                    <Link to={href} className="sb-ws__link" aria-current={on ? 'page' : undefined}>
                      <span className="sb-dot" aria-hidden="true" />
                      <span className="sb-ws__name">{w.name}</span>
                      {w.counts?.openItems ? (
                        <span className="sb-count" aria-label={`${w.counts.openItems} open items`}>
                          {w.counts.openItems}
                        </span>
                      ) : null}
                    </Link>
                  </div>
                  {isOpen && (
                    <ul className="sb-projects">
                      {kids.length === 0 && <li className="sb-projects__none">No projects yet</li>}
                      {kids.map(({ project: p, depth }) => {
                        const ph = `/projects/${encodeURIComponent(p.id)}`;
                        const pon = pathname === ph;
                        return (
                          <li key={p.id}>
                            <Link
                              to={ph}
                              aria-current={pon ? 'page' : undefined}
                              className={`sb-proj sb-proj--d${Math.min(depth, 3)}${pon ? ' sb-proj--on' : ''}`}
                            >
                              {p.name}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {SIDE_SECTIONS.map((s) => {
        if (!s.collapsible)
          return (
            <div key={s.key} className="nav sb-sec sb-sec--flat">
              {s.links.map((l) => item(l))}
            </div>
          );
        const key = `sec:${s.key}`;
        // A folded group opens itself while you are on one of its pages.
        const holdsActive = s.links.some((l) => l.to === active);
        const isOpen = open.has(key) || holdsActive;
        return (
          <section key={s.key} className="sb-sec">
            <button
              type="button"
              className="sb-group"
              aria-expanded={isOpen}
              onClick={() => toggle(key)}
            >
              {s.icon && <Icon name={s.icon} size={20} />}
              <span className="nav__label">{s.title}</span>
              <Icon name="chevron-right" size={14} className={`sb-group__chev${isOpen ? ' sb-group__chev--open' : ''}`} />
            </button>
            {isOpen && <div className="nav sb-sub">{s.links.map((l) => item(l))}</div>}
          </section>
        );
      })}

      <div className="nav sb-settings">{item(SETTINGS_LINK)}</div>
    </nav>
  );
}
